/**
 * Admin → All Users — system-wide user management.
 *
 * Reads `/admin/users/system` (every account incl. admins) and lets an
 * admin / super_admin: create users, edit role/FA/access (row → detail
 * drawer), bulk-apply changes, approve pending users, filter/sort, and
 * export CSV. super_admin guardrails live on the backend; 4xx → toast.
 */

import { useMemo, useState } from 'react'
import { ErrorCard } from '../components/ErrorCard'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Checkbox,
  Dropdown,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Spinner,
  Subtitle2,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  Add20Regular,
  ArrowDownload20Regular,
  ArrowSortDown20Regular,
  ArrowSortUp20Regular,
  CheckmarkCircle20Regular,
  MoreHorizontal20Regular,
  PersonAvailable20Regular,
  PersonProhibited20Regular,
  Search20Regular,
} from '@fluentui/react-icons'
import { FOCUS_AREAS } from '../types/focus_area'
import type { AdminUserCreate, AdminUserUpdate, SystemUser, UserRoleStr } from '../types/admin'
import {
  useBulkUpdateUsers,
  useCreateUser,
  useSystemUsers,
  useUpdateUser,
} from '../hooks/useAdminUsers'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { CreateUserDialog } from '../components/CreateUserDialog'
import { UserDetailDrawer } from '../components/UserDetailDrawer'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { downloadCsv } from '../lib/download'
import { activatable } from '../lib/activatable'

const ROLE_OPTIONS: { value: '' | UserRoleStr; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'council', label: 'Council' },
  { value: 'talent', label: 'Talent' },
  { value: 'member', label: 'Member' },
]

const ROLE_BADGE: Record<UserRoleStr, 'brand' | 'severe' | 'important' | 'informative'> = {
  super_admin: 'severe',
  admin: 'severe',
  council: 'brand',
  talent: 'informative',
  member: 'informative',
}
const ROLE_RANK: Record<string, number> = {
  super_admin: 0,
  admin: 1,
  council: 2,
  talent: 3,
  member: 3,
}

const BULK_ROLES: UserRoleStr[] = ['member', 'council', 'admin', 'super_admin']

type SortKey = 'name' | 'role' | 'fa' | 'created'

function errMsg(e: unknown, fallback: string): string {
  return apiErrorMessage(e, fallback)
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  headActions: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  searchWrap: { flex: '1 1 240px', minWidth: '200px' },
  bulkBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  tableWrap: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  sortable: { display: 'inline-flex', alignItems: 'center', gap: '2px', cursor: 'pointer' },
  nameCell: { cursor: 'pointer' },
  rowMeta: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  inactiveRow: { opacity: 0.55 },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    color: tokens.colorNeutralForeground3,
  },
})

export function AdminAllUsers() {
  const styles = useStyles()
  const { showToast } = useToast()
  const me = useEffectiveUser()
  const isSuper = me?.role === 'super_admin'

  const [q, setQ] = useState('')
  const [role, setRole] = useState<'' | UserRoleStr>('')
  const [includeTest, setIncludeTest] = useState(true)
  const [faFilter, setFaFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending' | 'approved'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  const [drawerUserId, setDrawerUserId] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useSystemUsers({ role, includeTest })
  const updateUser = useUpdateUser()
  const bulkUpdate = useBulkUpdateUsers()
  const createUser = useCreateUser()

  const rows = useMemo(() => {
    let r = data ?? []
    const needle = q.trim().toLowerCase()
    if (needle) {
      r = r.filter(
        (u) => u.name?.toLowerCase().includes(needle) || u.email?.toLowerCase().includes(needle),
      )
    }
    if (faFilter) r = r.filter((u) => (u.focus_area_code || '') === faFilter)
    if (activeFilter !== 'all') r = r.filter((u) => u.is_active === (activeFilter === 'active'))
    if (approvalFilter !== 'all') {
      r = r.filter((u) => (approvalFilter === 'pending' ? u.is_approved === false : u.is_approved))
    }
    const dir = sortDir === 'asc' ? 1 : -1
    return [...r].sort((a, b) => dir * cmp(a, b, sortKey))
  }, [data, q, faFilter, activeFilter, approvalFilter, sortKey, sortDir])

  const counts = useMemo(() => {
    const c: Record<string, number> = { total: rows.length, pending: 0 }
    for (const u of rows) {
      c[u.role] = (c[u.role] || 0) + 1
      if (u.is_approved === false) c.pending = (c.pending ?? 0) + 1
    }
    return c
  }, [rows])

  const visibleIds = useMemo(() => rows.map((u) => u.id), [rows])
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id))
  const someSelected = visibleIds.some((id) => selected.has(id)) && !allSelected

  function setSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    setSelected((prev) => (visibleIds.every((id) => prev.has(id)) ? new Set() : new Set(visibleIds)))
  }

  function patchUser(u: SystemUser, change: AdminUserUpdate, label: string) {
    updateUser.mutate(
      { id: u.id, change },
      {
        onSuccess: () => showToast({ title: label, intent: 'success' }),
        onError: (e) =>
          showToast({ title: 'Update failed', body: errMsg(e, label), intent: 'error' }),
      },
    )
  }

  function runBulk(change: AdminUserUpdate, label: string) {
    const ids = [...selected]
    if (ids.length === 0) return
    bulkUpdate.mutate(
      { ids, change },
      {
        onSuccess: (res) => {
          const errs = res.errors.length
          showToast({
            title: `${label}: ${res.updated} updated`,
            body: errs ? `${errs} skipped (guardrail/not found)` : undefined,
            intent: errs ? 'warning' : 'success',
          })
          setSelected(new Set())
        },
        onError: (e) =>
          showToast({ title: 'Bulk update failed', body: errMsg(e, label), intent: 'error' }),
      },
    )
  }

  function handleCreate(payload: AdminUserCreate) {
    createUser.mutate(payload, {
      onSuccess: () => {
        showToast({ title: `Created ${payload.name}`, intent: 'success' })
        setCreateOpen(false)
      },
      onError: (e) =>
        showToast({ title: 'Create failed', body: errMsg(e, 'Could not create'), intent: 'error' }),
    })
  }

  function exportCsv() {
    const headers = [
      'Name',
      'Email',
      'Role',
      'FA',
      'FA role',
      'Job',
      'Department',
      'Active',
      'Test',
      'Approved',
      'Created',
    ]
    const body = rows.map((u) => [
      u.name,
      u.email,
      u.role,
      u.focus_area_code,
      u.focus_area_role,
      u.job_title,
      u.department,
      u.is_active,
      u.is_test_account,
      u.is_approved,
      u.created_at,
    ])
    downloadCsv('daccelerate-users.csv', headers, body)
  }

  const SortHead = ({ k, label, width }: { k: SortKey; label: string; width: string }) => (
    <TableHeaderCell style={{ width }}>
      <span className={styles.sortable} onClick={() => setSort(k)}>
        {label}
        {sortKey === k &&
          (sortDir === 'asc' ? <ArrowSortUp20Regular /> : <ArrowSortDown20Regular />)}
      </span>
    </TableHeaderCell>
  )

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <Title2 block>All users</Title2>
          <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
            Create, manage, and approve every account.
          </Body1>
        </div>
        <div className={styles.headActions}>
          <Subtitle2>
            {counts.total ?? 0} shown · {counts.pending ?? 0} pending
          </Subtitle2>
          <Button icon={<ArrowDownload20Regular />} onClick={exportCsv} disabled={rows.length === 0}>
            Export
          </Button>
          <Button appearance="primary" icon={<Add20Regular />} onClick={() => setCreateOpen(true)}>
            New user
          </Button>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Input
            value={q}
            onChange={(_, d) => setQ(d.value)}
            contentBefore={<Search20Regular />}
            placeholder="Search name or email"
          />
        </div>
        <Dropdown
          value={ROLE_OPTIONS.find((o) => o.value === role)?.label || 'All roles'}
          selectedOptions={[role]}
          onOptionSelect={(_, d) => setRole((d.optionValue as '' | UserRoleStr) ?? '')}
        >
          {ROLE_OPTIONS.map((o) => (
            <Option key={o.value || 'all'} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          value={faFilter ? FOCUS_AREAS.find((f) => f.code === faFilter)?.name || faFilter : 'All FAs'}
          selectedOptions={[faFilter]}
          onOptionSelect={(_, d) => setFaFilter((d.optionValue as string) ?? '')}
        >
          <Option value="">All FAs</Option>
          {FOCUS_AREAS.map((f) => (
            <Option key={f.code} value={f.code} text={`${f.code} · ${f.name}`}>
              {f.code} · {f.name}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          value={
            activeFilter === 'all' ? 'Any status' : activeFilter === 'active' ? 'Active' : 'Inactive'
          }
          selectedOptions={[activeFilter]}
          onOptionSelect={(_, d) =>
            setActiveFilter((d.optionValue as 'all' | 'active' | 'inactive') ?? 'all')
          }
        >
          <Option value="all">Any status</Option>
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
        </Dropdown>
        <Dropdown
          value={
            approvalFilter === 'all'
              ? 'Any approval'
              : approvalFilter === 'pending'
                ? 'Pending'
                : 'Approved'
          }
          selectedOptions={[approvalFilter]}
          onOptionSelect={(_, d) =>
            setApprovalFilter((d.optionValue as 'all' | 'pending' | 'approved') ?? 'all')
          }
        >
          <Option value="all">Any approval</Option>
          <Option value="pending">Pending</Option>
          <Option value="approved">Approved</Option>
        </Dropdown>
        <Switch
          checked={includeTest}
          onChange={(_, d) => setIncludeTest(d.checked)}
          label="Include test"
        />
      </div>

      {selected.size > 0 && (
        <div className={styles.bulkBar}>
          <Subtitle2>{selected.size} selected</Subtitle2>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button size="small" appearance="primary">
                Set role
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {BULK_ROLES.map((r) => (
                  <MenuItem key={r} onClick={() => runBulk({ role: r }, `Set role → ${r}`)}>
                    {r}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button size="small">Set focus area</Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => runBulk({ focus_area_code: '' }, 'Clear FA')}>
                  — (clear)
                </MenuItem>
                {FOCUS_AREAS.map((f) => (
                  <MenuItem
                    key={f.code}
                    onClick={() => runBulk({ focus_area_code: f.code }, `Set FA → ${f.code}`)}
                  >
                    {f.code} · {f.name}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button size="small">Set FA role</Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => runBulk({ focus_area_role: 'lead' }, 'Set FA role → lead')}>
                  Lead
                </MenuItem>
                <MenuItem
                  onClick={() => runBulk({ focus_area_role: 'coach' }, 'Set FA role → coach')}
                >
                  Coach
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>

          <Button
            size="small"
            icon={<CheckmarkCircle20Regular />}
            onClick={() => runBulk({ is_approved: true }, 'Approve')}
          >
            Approve
          </Button>
          <Button
            size="small"
            icon={<PersonAvailable20Regular />}
            onClick={() => runBulk({ is_active: true }, 'Activate')}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<PersonProhibited20Regular />}
            onClick={() => runBulk({ is_active: false }, 'Deactivate')}
          >
            Deactivate
          </Button>
          <Button size="small" appearance="subtle" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className={styles.empty}>
          <Spinner size="small" />
        </div>
      ) : error ? (
        <div data-testid="all-users-error">
          <ErrorCard
            title="Couldn't load users"
            message={apiErrorMessage(error, 'Please try again.')}
            retry={() => {
              void refetch()
            }}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>No users match the current filters.</div>
      ) : (
        <div className={styles.tableWrap}>
          <Table aria-label="All users" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '36px' }}>
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'mixed' : false}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </TableHeaderCell>
                <SortHead k="name" label="User" width="26%" />
                <SortHead k="role" label="Role" width="12%" />
                <SortHead k="fa" label="FA" width="16%" />
                <TableHeaderCell style={{ width: '20%' }}>Job</TableHeaderCell>
                <TableHeaderCell style={{ width: '14%' }}>Status</TableHeaderCell>
                <TableHeaderCell style={{ width: '44px' }}> </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => {
                const fa = u.focus_area_code
                const faLabel = fa ? FOCUS_AREAS.find((f) => f.code === fa)?.name || fa : '—'
                return (
                  <TableRow key={u.id} className={!u.is_active ? styles.inactiveRow : undefined}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(u.id)}
                        onChange={() => toggle(u.id)}
                        aria-label={`Select ${u.name}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div
                        className={styles.nameCell}
                        role="button"
                        {...activatable(() => setDrawerUserId(u.id))}
                      >
                        <div>{u.name || '(no name)'}</div>
                        <div className={styles.rowMeta}>{u.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge appearance="outline" color={ROLE_BADGE[u.role] || 'informative'}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>{faLabel}</div>
                      {u.focus_area_role && <div className={styles.rowMeta}>{u.focus_area_role}</div>}
                    </TableCell>
                    <TableCell>
                      <div>{u.job_title || '—'}</div>
                      {u.department && <div className={styles.rowMeta}>{u.department}</div>}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.is_approved === false && (
                          <Badge appearance="tint" color="warning" size="small">
                            PENDING
                          </Badge>
                        )}
                        {!u.is_active && (
                          <Badge appearance="tint" color="danger" size="small">
                            INACTIVE
                          </Badge>
                        )}
                        {u.is_test_account && (
                          <Badge appearance="tint" color="warning" size="small">
                            TEST
                          </Badge>
                        )}
                        {u.is_active && u.is_approved !== false && !u.is_test_account && (
                          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                            active
                          </Caption1>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<MoreHorizontal20Regular />}
                            aria-label={`Actions for ${u.name}`}
                          />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem onClick={() => setDrawerUserId(u.id)}>Open…</MenuItem>
                            {u.is_approved === false && (
                              <MenuItem
                                icon={<CheckmarkCircle20Regular />}
                                onClick={() =>
                                  patchUser(u, { is_approved: true }, `Approved ${u.name}`)
                                }
                              >
                                Approve
                              </MenuItem>
                            )}
                            {u.is_active ? (
                              <MenuItem
                                icon={<PersonProhibited20Regular />}
                                onClick={() =>
                                  patchUser(u, { is_active: false }, `Deactivated ${u.name}`)
                                }
                              >
                                Deactivate
                              </MenuItem>
                            ) : (
                              <MenuItem
                                icon={<PersonAvailable20Regular />}
                                onClick={() =>
                                  patchUser(u, { is_active: true }, `Reactivated ${u.name}`)
                                }
                              >
                                Reactivate
                              </MenuItem>
                            )}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        canCreateAdminLevel={isSuper}
        pending={createUser.isPending}
      />
      <UserDetailDrawer
        userId={drawerUserId}
        open={drawerUserId !== null}
        onOpenChange={(o) => !o && setDrawerUserId(null)}
      />
    </div>
  )
}

function cmp(a: SystemUser, b: SystemUser, key: SortKey): number {
  switch (key) {
    case 'role':
      return (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9)
    case 'fa':
      return (a.focus_area_code || '').localeCompare(b.focus_area_code || '')
    case 'created':
      return (a.created_at || '').localeCompare(b.created_at || '')
    case 'name':
    default:
      return (a.name || '').localeCompare(b.name || '')
  }
}
